/**
 * Wearable Service
 * Handles wearable device connections (Fitbit, Apple Health, etc.)
 */

// In Cloudflare Pages, API routes are served from the same domain
// Use relative URLs in production, absolute URLs in development
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string means same origin (Cloudflare Pages Functions)
  : 'http://localhost:3001';

/** Generic message returned to client on errors (do not expose error.message). */
const USER_FACING_ERROR = 'Something went wrong. Please try again.';

/** Get Authorization header with current session access token for API calls that require auth. */
async function getAuthHeaders() {
  const { supabase } = await import('../supabase');
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

class WearableService {
  /**
   * Get Fitbit connection status for current user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Connection status
   */
  async getFitbitConnection(userId) {
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      const supabase = createClient(supabaseUrl, supabaseAnonKey);
      
      const { data, error } = await supabase
        .from('wearable_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', 'fitbit')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        throw new Error(error.message || 'Failed to check connection');
      }

      return {
        success: true,
        connected: !!data,
        connection: data || null
      };
    } catch (error) {
      console.error('Error checking Fitbit connection:', error);
      return {
        success: false,
        connected: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Initiate Fitbit OAuth flow
   * Requires valid session - server verifies JWT and uses that user ID (never trusts client).
   * @param {string} userId - User ID (used for UX check; server uses JWT)
   * @returns {Promise<Object>} - Redirect URL or error
   */
  async connectFitbit(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/fitbit/authorize`, {
        method: 'POST',
        headers: await getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to initiate Fitbit connection');
      }

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      }

      return {
        success: true,
        message: 'Redirecting to Fitbit...'
      };
    } catch (error) {
      console.error('Error connecting Fitbit:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Disconnect Fitbit
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Success or error
   */
  async disconnectFitbit(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/fitbit/disconnect`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect Fitbit');
      }

      return {
        success: true,
        message: data.message || 'Fitbit disconnected successfully'
      };
    } catch (error) {
      console.error('Error disconnecting Fitbit:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Manually trigger data sync for Fitbit
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Sync results
   */
  async syncFitbitData(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/fitbit/sync`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: userId }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync data');
      }

      return {
        success: true,
        data: data.results || data,
        message: 'Data synced successfully'
      };
    } catch (error) {
      console.error('Error syncing Fitbit data:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Get Garmin connection status for current user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Connection status
   */
  async getGarminConnection(userId) {
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      const supabase = createClient(supabaseUrl, supabaseAnonKey);

      const { data, error } = await supabase
        .from('wearable_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', 'garmin')
        .eq('is_active', true)
        .maybeSingle();

      if (error) {
        throw new Error(error.message || 'Failed to check connection');
      }

      return {
        success: true,
        connected: !!data,
        connection: data || null
      };
    } catch (error) {
      console.error('Error checking Garmin connection:', error);
      return {
        success: false,
        connected: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Initiate Garmin OAuth flow (PKCE)
   * Requires valid session - server verifies JWT and uses that user ID (never trusts client).
   * @param {string} userId - User ID (used for UX check; server uses JWT)
   * @returns {Promise<Object>} - Redirect URL or error
   */
  async connectGarmin(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/garmin/authorize`, {
        method: 'POST',
        headers: await getAuthHeaders()
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to initiate Garmin connection');
      }

      if (data.redirectUrl) {
        window.location.href = data.redirectUrl;
      }

      return {
        success: true,
        message: 'Redirecting to Garmin...'
      };
    } catch (error) {
      console.error('Error connecting Garmin:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Disconnect Garmin
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Success or error
   */
  async disconnectGarmin(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/garmin/disconnect`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: userId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect Garmin');
      }

      return {
        success: true,
        message: data.message || 'Garmin disconnected successfully'
      };
    } catch (error) {
      console.error('Error disconnecting Garmin:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Manually trigger data sync for Garmin
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - Sync results
   */
  async syncGarminData(userId) {
    try {
      if (!userId) {
        throw new Error('User ID is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/garmin/sync`, {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ user_id: userId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to sync data');
      }

      return {
        success: true,
        data: data.results || data,
        message: 'Data synced successfully'
      };
    } catch (error) {
      console.error('Error syncing Garmin data:', error);
      return {
        success: false,
        error: USER_FACING_ERROR
      };
    }
  }

  /**
   * Get all wearable connections for a user
   * @param {string} userId - User ID
   * @returns {Promise<Object>} - List of connections
   */
  async getConnections(userId) {
    try {
      const { createClient } = await import('@supabase/supabase-js');
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      const supabase = createClient(supabaseUrl, supabaseAnonKey);
      
      const { data, error } = await supabase
        .from('wearable_connections')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('connected_at', { ascending: false });

      if (error) {
        throw new Error(error.message || 'Failed to fetch connections');
      }

      return {
        success: true,
        connections: data || []
      };
    } catch (error) {
      console.error('Error fetching connections:', error);
      return {
        success: false,
        connections: [],
        error: USER_FACING_ERROR
      };
    }
  }
}

// Create and export a singleton instance
const wearableService = new WearableService();
export default wearableService;

